import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Save,
  RefreshCw,
  Download,
  Upload,
  Search,
  Filter,
  CheckCircle,
  AlertTriangle,
  Database,
  Network,
  Monitor,
  Users,
  Shield,
  Code,
  Hash
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { formatRelativeTime } from '../utils/format'

interface ConfigSection {
  id: string;
  name: string;
  description: string;
  category: 'system' | 'network' | 'security' | 'monitoring' | 'user' | 'storage';
  lastModified: Date;
  modifiedBy: string;
  enabled: boolean;
  critical: boolean;
}

interface ConfigItem {
  id: string;
  sectionId: string;
  key: string;
  value: string | number | boolean;
  type: 'string' | 'number' | 'boolean' | 'json' | 'enum';
  description: string;
  defaultValue: string | number | boolean;
  required: boolean;
  validation?: string;
  options?: string[];
  sensitive: boolean;
  restartRequired: boolean;
  lastModified: Date;
  modifiedBy: string;
}

// Mock data
const mockConfigSections: ConfigSection[] = [
  {
    id: 'section-1',
    name: 'Consensus Algorithm',
    description: 'Configuration for the distributed consensus mechanism',
    category: 'system',
    lastModified: new Date(Date.now() - 2 * 60 * 60 * 1000),
    modifiedBy: 'admin@acme-corp.com',
    enabled: true,
    critical: true
  },
  {
    id: 'section-2',
    name: 'Network Settings',
    description: 'Node communication and networking configuration',
    category: 'network',
    lastModified: new Date(Date.now() - 6 * 60 * 60 * 1000),
    modifiedBy: 'network-admin@acme-corp.com',
    enabled: true,
    critical: true
  },
  {
    id: 'section-3',
    name: 'Security Policies',
    description: 'Authentication, authorization, and encryption settings',
    category: 'security',
    lastModified: new Date(Date.now() - 12 * 60 * 60 * 1000),
    modifiedBy: 'security@acme-corp.com',
    enabled: true,
    critical: true
  },
  {
    id: 'section-4',
    name: 'Monitoring & Alerts',
    description: 'Performance monitoring and alerting configuration',
    category: 'monitoring',
    lastModified: new Date(Date.now() - 24 * 60 * 60 * 1000),
    modifiedBy: 'ops@acme-corp.com',
    enabled: true,
    critical: false
  },
  {
    id: 'section-5',
    name: 'Storage Configuration',
    description: 'Distributed storage and replication settings',
    category: 'storage',
    lastModified: new Date(Date.now() - 48 * 60 * 60 * 1000),
    modifiedBy: 'storage-admin@acme-corp.com',
    enabled: true,
    critical: false
  },
  {
    id: 'section-6',
    name: 'User Management',
    description: 'User authentication and authorization settings',
    category: 'user',
    lastModified: new Date(Date.now() - 72 * 60 * 60 * 1000),
    modifiedBy: 'admin@acme-corp.com',
    enabled: true,
    critical: false
  }
];

const mockConfigItems: ConfigItem[] = [
  // Consensus Algorithm settings
  {
    id: 'config-1',
    sectionId: 'section-1',
    key: 'consensus.algorithm',
    value: 'raft',
    type: 'enum',
    description: 'The consensus algorithm to use for distributed agreement',
    defaultValue: 'raft',
    required: true,
    options: ['raft', 'pbft', 'pow'],
    sensitive: false,
    restartRequired: true,
    lastModified: new Date(Date.now() - 2 * 60 * 60 * 1000),
    modifiedBy: 'admin@acme-corp.com'
  },
  {
    id: 'config-2',
    sectionId: 'section-1',
    key: 'consensus.election_timeout',
    value: 5000,
    type: 'number',
    description: 'Leader election timeout in milliseconds',
    defaultValue: 5000,
    required: true,
    validation: 'min:1000,max:30000',
    sensitive: false,
    restartRequired: false,
    lastModified: new Date(Date.now() - 2 * 60 * 60 * 1000),
    modifiedBy: 'admin@acme-corp.com'
  },
  // Network settings
  {
    id: 'config-3',
    sectionId: 'section-2',
    key: 'network.max_peers',
    value: 100,
    type: 'number',
    description: 'Maximum number of peer connections',
    defaultValue: 50,
    required: true,
    validation: 'min:10,max:1000',
    sensitive: false,
    restartRequired: true,
    lastModified: new Date(Date.now() - 6 * 60 * 60 * 1000),
    modifiedBy: 'network-admin@acme-corp.com'
  },
  {
    id: 'config-4',
    sectionId: 'section-2',
    key: 'network.heartbeat_interval',
    value: 1000,
    type: 'number',
    description: 'Heartbeat interval between nodes in milliseconds',
    defaultValue: 1000,
    required: true,
    validation: 'min:100,max:10000',
    sensitive: false,
    restartRequired: false,
    lastModified: new Date(Date.now() - 6 * 60 * 60 * 1000),
    modifiedBy: 'network-admin@acme-corp.com'
  },
  // Security settings
  {
    id: 'config-5',
    sectionId: 'section-3',
    key: 'security.encryption_enabled',
    value: true,
    type: 'boolean',
    description: 'Enable end-to-end encryption for all communications',
    defaultValue: true,
    required: true,
    sensitive: false,
    restartRequired: true,
    lastModified: new Date(Date.now() - 12 * 60 * 60 * 1000),
    modifiedBy: 'security@acme-corp.com'
  },
  {
    id: 'config-6',
    sectionId: 'section-3',
    key: 'security.jwt_secret',
    value: '***HIDDEN***',
    type: 'string',
    description: 'JWT signing secret for authentication tokens',
    defaultValue: '',
    required: true,
    sensitive: true,
    restartRequired: true,
    lastModified: new Date(Date.now() - 12 * 60 * 60 * 1000),
    modifiedBy: 'security@acme-corp.com'
  }
];

export const ConfigurationPage: React.FC = () => {
  const [configSections] = useState<ConfigSection[]>(mockConfigSections);
  const [configItems] = useState<ConfigItem[]>(mockConfigItems);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showCriticalOnly, setShowCriticalOnly] = useState(false);

  const filteredSections = useMemo(() => {
    return configSections.filter(section => {
      const matchesSearch = section.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           section.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === 'all' || section.category === categoryFilter;
      const matchesCritical = !showCriticalOnly || section.critical;

      return matchesSearch && matchesCategory && matchesCritical;
    });
  }, [configSections, searchTerm, categoryFilter, showCriticalOnly]);

  const sectionItems = useMemo(() => {
    if (!selectedSection) return [];
    return configItems.filter(item => item.sectionId === selectedSection);
  }, [configItems, selectedSection]);

  const getCategoryIcon = (category: ConfigSection['category']) => {
    switch (category) {
      case 'system': return <Settings className="w-4 h-4" />;
      case 'network': return <Network className="w-4 h-4" />;
      case 'security': return <Shield className="w-4 h-4" />;
      case 'monitoring': return <Monitor className="w-4 h-4" />;
      case 'storage': return <Database className="w-4 h-4" />;
      case 'user': return <Users className="w-4 h-4" />;
      default: return <Settings className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: ConfigSection['category']): 'default' | 'success' | 'warning' | 'danger' => {
    switch (category) {
      case 'system': return 'default';
      case 'network': return 'success';
      case 'security': return 'danger';
      case 'monitoring': return 'warning';
      case 'storage': return 'default';
      case 'user': return 'success';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: ConfigItem['type']) => {
    switch (type) {
      case 'string': return <Code className="w-3 h-3" />;
      case 'number': return <Hash className="w-3 h-3" />;
      case 'boolean': return <CheckCircle className="w-3 h-3" />;
      case 'json': return <Code className="w-3 h-3" />;
      case 'enum': return <Filter className="w-3 h-3" />;
      default: return <Code className="w-3 h-3" />;
    }
  };

  const formatConfigValue = (item: ConfigItem): string => {
    if (item.sensitive) return '***HIDDEN***';
    
    switch (item.type) {
      case 'boolean': return item.value ? 'true' : 'false';
      case 'json': return JSON.stringify(item.value, null, 2);
      default: return String(item.value);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-console-text">Configuration Management</h1>
          <p className="text-console-muted mt-1">Manage system configuration and settings</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button variant="secondary">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="secondary">
            <RefreshCw className="w-4 h-4 mr-2" />
            Reload
          </Button>
          <Button>
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-grid">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Total Sections</p>
                <p className="text-2xl font-bold text-console-text">{configSections.length}</p>
              </div>
              <Settings className="w-6 h-6 text-console-accent" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Critical Configs</p>
                <p className="text-2xl font-bold text-red-500">{configSections.filter(s => s.critical).length}</p>
              </div>
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Config Items</p>
                <p className="text-2xl font-bold text-console-text">{configItems.length}</p>
              </div>
              <Code className="w-6 h-6 text-console-accent" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Categories</p>
                <p className="text-2xl font-bold text-console-text">{new Set(configSections.map(s => s.category)).size}</p>
              </div>
              <Filter className="w-6 h-6 text-console-accent" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4 p-4 bg-console-surface rounded-lg">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-console-muted" />
            <input
              type="text"
              placeholder="Search configuration sections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-console-bg border border-console-border rounded-lg text-console-text placeholder-console-muted focus:ring-2 focus:ring-console-accent focus:border-transparent"
            />
          </div>
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 bg-console-bg border border-console-border rounded-lg text-console-text focus:ring-2 focus:ring-console-accent focus:border-transparent"
        >
          <option value="all">All Categories</option>
          <option value="system">System</option>
          <option value="network">Network</option>
          <option value="security">Security</option>
          <option value="monitoring">Monitoring</option>
          <option value="storage">Storage</option>
          <option value="user">User</option>
        </select>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowCriticalOnly(!showCriticalOnly)}
          className={showCriticalOnly ? 'bg-red-500/20 border-red-500/50' : ''}
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Critical Only
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Configuration Sections */}
        <div className="lg:col-span-1 space-y-4">
          <h2 className="text-lg font-semibold text-console-text">Configuration Sections</h2>
          
          {filteredSections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card 
                className={`cursor-pointer transition-all ${
                  selectedSection === section.id ? 'ring-2 ring-console-accent' : ''
                }`}
                onClick={() => setSelectedSection(section.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(section.category)}
                      {section.critical && <AlertTriangle className="w-3 h-3 text-red-500" />}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-console-text">{section.name}</h3>
                        <Badge variant={getCategoryColor(section.category)} size="sm">
                          {section.category}
                        </Badge>
                      </div>
                      
                      <p className="text-xs text-console-muted mb-2">{section.description}</p>
                      
                      <div className="flex items-center justify-between text-xs text-console-muted">
                        <span>By {section.modifiedBy}</span>
                        <span>{formatRelativeTime(section.lastModified)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Configuration Details */}
        <div className="lg:col-span-2">
          {selectedSection ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-console-text">Configuration Items</h2>
                <Button size="sm">
                  Add Item
                </Button>
              </div>

              {sectionItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getTypeIcon(item.type)}
                            <h3 className="text-sm font-semibold text-console-text">{item.key}</h3>
                            <Badge variant="default" size="sm">{item.type}</Badge>
                            {item.required && <Badge variant="danger" size="sm">Required</Badge>}
                            {item.sensitive && <Badge variant="warning" size="sm">Sensitive</Badge>}
                            {item.restartRequired && <Badge variant="warning" size="sm">Restart Required</Badge>}
                          </div>
                          
                          <p className="text-xs text-console-muted mb-3">{item.description}</p>
                          
                          <div className="space-y-2">
                            <div>
                              <span className="text-xs text-console-muted">Current Value:</span>
                              <code className="ml-2 px-2 py-1 bg-console-bg rounded text-xs text-console-text">
                                {formatConfigValue(item)}
                              </code>
                            </div>
                            
                            <div>
                              <span className="text-xs text-console-muted">Default:</span>
                              <code className="ml-2 px-2 py-1 bg-console-surface rounded text-xs text-console-muted">
                                {String(item.defaultValue)}
                              </code>
                            </div>

                            {item.validation && (
                              <div>
                                <span className="text-xs text-console-muted">Validation:</span>
                                <code className="ml-2 text-xs text-orange-400">{item.validation}</code>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-4 mt-3 text-xs text-console-muted">
                            <span>Modified by {item.modifiedBy}</span>
                            <span>{formatRelativeTime(item.lastModified)}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button variant="secondary" size="sm">
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Settings className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Settings className="w-12 h-12 text-console-muted mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-console-text mb-2">Select a Configuration Section</h3>
                <p className="text-console-muted">Choose a configuration section from the left panel to view and edit its settings.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};